export interface WalletSnapshot {
  readonly balance: number;
  readonly lastDelta: number;
}

export type WalletListener = (snap: WalletSnapshot) => void;

export class PlayerWallet {
  private _balance = 0;
  private _lastDelta = 0;
  private readonly listeners = new Set<WalletListener>();

  constructor(initialBalance = 0) {
    this._balance = initialBalance;
  }

  get balance(): number {
    return this._balance;
  }

  credit(amount: number): number {
    if (amount <= 0) return 0;
    this._balance += amount;
    this._lastDelta = amount;
    this.emit();
    return amount;
  }

  debit(amount: number): boolean {
    if (amount <= 0) return true;
    if (this._balance < amount) return false;
    this._balance -= amount;
    this._lastDelta = -amount;
    this.emit();
    return true;
  }

  canAfford(amount: number): boolean {
    return this._balance >= amount;
  }

  /** Force-set the balance — used by the save/load system. Emits to
   *  listeners so the UI redraws. */
  setBalance(amount: number): void {
    const next = Math.max(0, Math.floor(amount));
    if (this._balance === next) return;
    this._lastDelta = next - this._balance;
    this._balance = next;
    this.emit();
  }

  subscribe(listener: WalletListener): () => void {
    this.listeners.add(listener);
    listener(this.snapshot());
    return () => {
      this.listeners.delete(listener);
    };
  }

  snapshot(): WalletSnapshot {
    return {
      balance: this._balance,
      lastDelta: this._lastDelta
    };
  }

  private emit(): void {
    const snap = this.snapshot();
    for (const listener of this.listeners) listener(snap);
  }
}
